import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Designer } from "@pdfme/ui";
import {
  text,
  multiVariableText,
  image,
  svg,
  table,
  barcodes,
  line,
  rectangle,
  ellipse,
  dateTime,
  date,
  time,
  select,
  radioGroup,
  checkbox,
} from "@pdfme/schemas";
import type { Template } from "@pdfme/common";
import { fetchTemplate, saveTemplate } from "../api/client.js";
import { NewTemplateDialog } from "./NewTemplateDialog.js";
import { SaveTemplateDialog } from "./SaveTemplateDialog.js";
import { GeneratePdfDialog } from "./GeneratePdfDialog.js";
import type { NewTemplateInfo, TemplateDetail } from "../types/index.js";
import { HeaderFooterDialog } from "./HeaderFooterDialog.js";
import type { StaticSchemaEntry } from "./HeaderFooterDialog.js";
import { loadPdfmeFonts } from "../pdf/fonts.js";
import { bleedInTemplateUnits, fromEditorView, toEditorView } from "../pdf/bleedView.js";

const PLUGINS = {
  Text: text,
  "Multi-Variable Text": multiVariableText,
  Image: image,
  SVG: svg,
  Table: table,
  Line: line,
  Rectangle: rectangle,
  Ellipse: ellipse,
  DateTime: dateTime,
  Date: date,
  Time: time,
  Select: select,
  RadioGroup: radioGroup,
  Checkbox: checkbox,
  QRCode: barcodes.qrcode,
  EAN13: barcodes.ean13,
  EAN8: barcodes.ean8,
  Code39: barcodes.code39,
  Code128: barcodes.code128,
  NW7: barcodes.nw7,
  ITF14: barcodes.itf14,
  UPCA: barcodes.upca,
  UPCE: barcodes.upce,
  GS1DataMatrix: barcodes.gs1datamatrix,
  PDF417: barcodes.pdf417,
  JapanPost: barcodes.japanpost,
};

function createBlankTemplate(widthMm: number, heightMm: number): Template {
  // pdfme measures the page in mm (positions and sizes too); font sizes are in pt.
  // Older versions stored points here, which made labels 2.83x too large.
  return {
    basePdf: { width: widthMm, height: heightMm, padding: [0, 0, 0, 0] },
    schemas: [[]],
  };
}

type SchemaField = { name: string; type: string; [key: string]: unknown };

function getSampleValue(schema: SchemaField): string | string[][] {
  switch (schema.type) {
    case "Text":
    case "Multi-Variable Text":
      return `Sample ${schema.name}`;
    case "Image":
      return "https://example.com/image.png";
    case "SVG":
      return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="40"/></svg>';
    case "Table": {
      // pdfme table content = data rows only; headers come from schema.head
      const rawHead = schema["head"];
      const cols: string[] =
        Array.isArray(rawHead) && rawHead.length > 0
          ? rawHead.map(String)
          : ["Column 1", "Column 2", "Column 3"];
      return [
        cols.map((col) => `${col} value 1`),
        cols.map((col) => `${col} value 2`),
        cols.map((col) => `${col} value 3`),
      ];
    }
    case "QRCode":
      return "https://example.com";
    case "EAN13":
      return "5901234123457";
    case "EAN8":
      return "96385074";
    case "Code39":
    case "Code128":
      return "SAMPLE123";
    case "NW7":
      return "A0123456789A";
    case "ITF14":
      return "12345678901231";
    case "UPCA":
      return "012345678905";
    case "UPCE":
      return "01234565";
    case "GS1DataMatrix":
    case "PDF417":
      return "SAMPLE123";
    case "JapanPost":
      return "1234567-8";
    case "DateTime":
      return new Date().toISOString().slice(0, 16);
    case "Date":
      return new Date().toISOString().split("T")[0] ?? "";
    case "Time":
      return "12:00";
    case "Select":
    case "RadioGroup": {
      const opts = schema["options"];
      return Array.isArray(opts) && opts.length > 0
        ? String(opts[0])
        : "option1";
    }
    case "Checkbox":
      return "true";
    case "Line":
    case "Rectangle":
    case "Ellipse":
      return "";
    default:
      return `Sample ${schema.name}`;
  }
}

function buildFieldsExport(
  template: Template,
  templateId: string | undefined,
  versionId: string | undefined,
): string {
  const schemas = template.schemas as SchemaField[][];
  const sampleData: Record<string, string | string[][]> = {};

  for (const page of schemas) {
    for (const schema of page) {
      if (schema.name) {
        sampleData[schema.name] = getSampleValue(schema);
      }
    }
  }

  const payload: Record<string, unknown> = {
    templateId: templateId ?? "your-template-id",
    ...(versionId ? { versionId } : {}),
    data: [sampleData],
  };

  return JSON.stringify(payload, null, 2);
}

function triggerDownload(filename: string, content: string): void {
  const blob = new Blob([content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type ReadyState = {
  phase: "ready";
  templateInfo: NewTemplateInfo;
  existingId?: string;
  currentVersionId?: string;
  initialTemplate: Template;
  /** print bleed per side in mm (template value or server default), shown as red frame */
  bleedMm: number;
};

type PageState =
  | { phase: "loading" }
  | { phase: "new-template-dialog" }
  | ReadyState
  | { phase: "error"; message: string };

export function TemplateDesigner() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const versionId = searchParams.get("versionId") ?? undefined;
  const navigate = useNavigate();

  const containerRef = useRef<HTMLDivElement>(null);
  const designerRef = useRef<Designer | null>(null);

  const [pageState, setPageState] = useState<PageState>(
    id ? { phase: "loading" } : { phase: "new-template-dialog" },
  );
  const [staticSchemas, setStaticSchemas] = useState<StaticSchemaEntry[]>([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showHeaderFooterDialog, setShowHeaderFooterDialog] = useState(false);
  const [generateTemplate, setGenerateTemplate] = useState<Template | null>(
    null,
  );
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    fetchTemplate(id, versionId)
      .then((detail: TemplateDetail) => {
        if (cancelled) return;
        const loadedTemplate = detail.template as Template;
        const basePdf = loadedTemplate.basePdf as Record<string, unknown>;
        const existing = Array.isArray(basePdf["staticSchema"])
          ? (basePdf["staticSchema"] as StaticSchemaEntry[])
          : [];
        setStaticSchemas(existing);
        setPageState({
          phase: "ready",
          existingId: id,
          currentVersionId: detail.version.versionId,
          templateInfo: {
            label: detail.meta.label,
            labelWidthMm: detail.meta.labelWidthMm,
            labelHeightMm: detail.meta.labelHeightMm,
            description: detail.meta.description,
          },
          initialTemplate: loadedTemplate,
          bleedMm: typeof detail.bleedMm === "number" ? detail.bleedMm : 0,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPageState({
          phase: "error",
          message: err instanceof Error ? err.message : String(err),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [id, versionId]);

  useEffect(() => {
    if (pageState.phase !== "ready") return;
    if (!containerRef.current) return;

    const container = containerRef.current;
    let designer: Designer | null = null;
    let cancelled = false;
    // fonts are fetched first and handed over as data: pdfme 6 only fetches font URLs on public hosts
    void loadPdfmeFonts().then((font) => {
      if (cancelled) return;
      designer = new Designer({
        domContainer: container,
        template: toEditorView(pageState.initialTemplate, bleedUnits()),
        options: { lang: "en", font },
        plugins: PLUGINS,
      });
      designerRef.current = designer;
    });

    return () => {
      cancelled = true;
      designer?.destroy();
      designerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState.phase === "ready" ? pageState.initialTemplate : null]);

  /** bleed in template units (mm; points for old templates), 0 outside the ready state */
  function bleedUnits(): number {
    if (pageState.phase !== "ready") return 0;
    return bleedInTemplateUnits(pageState.initialTemplate, pageState.bleedMm, pageState.templateInfo.labelWidthMm);
  }

  function templateWithStaticSchemas(): Template {
    const template = fromEditorView(designerRef.current!.getTemplate(), bleedUnits());
    if (staticSchemas.length === 0) return template;
    const basePdf = template.basePdf as Record<string, unknown>;
    return {
      ...template,
      basePdf: {
        ...basePdf,
        staticSchema: staticSchemas,
      } as unknown as Template["basePdf"],
    };
  }

  function handleNewTemplateConfirm(info: NewTemplateInfo) {
    setPageState({
      phase: "ready",
      templateInfo: info,
      initialTemplate: createBlankTemplate(
        info.labelWidthMm,
        info.labelHeightMm,
      ),
      bleedMm: 0,
    });
  }

  async function handleSaveConfirm(comment: string, createdBy: string) {
    if (!designerRef.current || pageState.phase !== "ready") return;

    const template = templateWithStaticSchemas();
    const { templateInfo, existingId } = pageState;

    try {
      const result = await saveTemplate({
        id: existingId,
        label: templateInfo.label,
        labelWidthMm: templateInfo.labelWidthMm,
        labelHeightMm: templateInfo.labelHeightMm,
        description: templateInfo.description,
        comment,
        createdBy,
        template: template as Record<string, unknown>,
      });

      setShowSaveDialog(false);
      setSaveSuccess(`Saved as version ${result.versionId}`);
      setTimeout(() => setSaveSuccess(null), 4000);

      setPageState((prev) =>
        prev.phase === "ready"
          ? { ...prev, currentVersionId: result.versionId }
          : prev,
      );

      if (!existingId) {
        navigate(`/templates/${result.templateId}/edit`, { replace: true });
      }
    } catch (err) {
      alert(`Save failed: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  function handleExportFields() {
    if (!designerRef.current || pageState.phase !== "ready") return;
    const template = templateWithStaticSchemas();
    const { existingId, currentVersionId } = pageState;
    const json = buildFieldsExport(template, existingId, currentVersionId);
    triggerDownload(`${existingId ?? "template"}_fields_sample.json`, json);
  }

  function handleOpenGenerateDialog() {
    if (!designerRef.current || pageState.phase !== "ready") return;
    setGenerateTemplate(templateWithStaticSchemas());
  }

  if (pageState.phase === "loading") {
    return <div className="status-msg">Loading template…</div>;
  }

  if (pageState.phase === "error") {
    return (
      <div className="status-msg status-msg--error">
        Error: {pageState.message}
      </div>
    );
  }

  if (pageState.phase === "new-template-dialog") {
    return (
      <NewTemplateDialog
        onConfirm={handleNewTemplateConfirm}
        onCancel={() => navigate("/")}
      />
    );
  }

  const { templateInfo, existingId } = pageState;

  return (
    <div className="designer-page">
      <div className="designer-toolbar">
        <div className="designer-toolbar__info">
          <span className="designer-toolbar__label">{templateInfo.label}</span>
          <span className="designer-toolbar__size">
            {templateInfo.labelWidthMm} × {templateInfo.labelHeightMm} mm
          </span>
          <span
            className="designer-toolbar__size"
            title="Print bleed per side, drawn as red frame around the label. Coloured rectangles and ellipses that touch the label edge are stretched into it when labels are generated; images, text and barcodes only go into it where you drag them. Set it in the template list (column Bleed)."
          >
            {pageState.bleedMm > 0 ? `Bleed ${pageState.bleedMm} mm (red frame)` : "No bleed"}
          </span>
          {existingId && (
            <button
              className="btn btn--sm btn--ghost"
              onClick={() => navigate(`/templates/${existingId}/versions`)}
            >
              Version History
            </button>
          )}
        </div>
        <div className="designer-toolbar__actions">
          {saveSuccess && <span className="save-success">{saveSuccess}</span>}
          <button
            className="btn btn--sm btn--ghost"
            onClick={() => setShowHeaderFooterDialog(true)}
            title="Manage header and footer elements (static, shown on every page)"
          >
            ☰ Header/Footer
            {staticSchemas.length > 0 && (
              <span className="hf-badge">{staticSchemas.length}</span>
            )}
          </button>
          <button
            className="btn btn--sm btn--ghost"
            onClick={handleExportFields}
            title="Download a sample JSON payload for the generate API"
          >
            ↓ Export Fields
          </button>
          <button
            className="btn btn--sm btn--ghost"
            onClick={handleOpenGenerateDialog}
            title="Generate a PDF from this template with custom field values"
          >
            ▶ Generate PDF
          </button>
          <button className="btn" onClick={() => navigate("/")}>
            Back
          </button>
          <button
            className="btn btn--primary"
            onClick={() => setShowSaveDialog(true)}
          >
            Save
          </button>
        </div>
      </div>

      <div ref={containerRef} className="designer-container" />

      {showSaveDialog && (
        <SaveTemplateDialog
          templateLabel={templateInfo.label}
          onConfirm={handleSaveConfirm}
          onCancel={() => setShowSaveDialog(false)}
        />
      )}

      {showHeaderFooterDialog && pageState.phase === "ready" && (
        <HeaderFooterDialog
          entries={staticSchemas}
          pageWidthPt={
            (pageState.initialTemplate.basePdf as { width: number }).width
          }
          pageHeightPt={
            (pageState.initialTemplate.basePdf as { height: number }).height
          }
          onChange={setStaticSchemas}
          onClose={() => setShowHeaderFooterDialog(false)}
        />
      )}

      {generateTemplate && existingId && (
        <GeneratePdfDialog
          templateId={existingId}
          versionId={pageState.currentVersionId}
          template={generateTemplate}
          onClose={() => setGenerateTemplate(null)}
        />
      )}
    </div>
  );
}
